// Events on a time-series visual — the TypeScript twin of the engine's
// normaliser (packages/runtime-engine/twasta_runtime_engine/visual_events/
// model.py) and of the Java ai.twasta.engine.visuals.VisualEvents. All three
// answer test-script/visual_event_fixtures/cases.json identically
// (test-script/test_visual_events_overlay.mjs runs this one).
//
// The choices and limits are DATA: visual_events_config.json (a byte-identical
// copy of the engine's, checked by test-script/test_visual_events_model.py).

import CONFIG from './visual_events_config.json'

export type EventKind = 'manual' | 'column' | 'query' | 'notes'
export type EventLook = 'auto' | 'line' | 'band' | 'flag'
export type EventLabels = 'hover' | 'always'

export interface EventItem {
  date: string
  end: string
  label: string
  category: string
  note: string
  // Notes only (server-filled):
  id?: number
  by?: string
  by_name?: string
  mine?: boolean
  at?: string
}

export interface EventMap {
  date: string
  end: string
  label: string
  category: string
  note: string
}

export interface EventLayer {
  id: string
  kind: EventKind
  name: string
  color: string
  look: EventLook
  labels: EventLabels
  hidden: boolean
  items?: EventItem[]
  column?: string
  sql?: string
  map?: EventMap
  who_can_add?: 'viewers' | 'admins'
  // Public (server-resolved) fields:
  outside?: number
  error?: string
  can_add?: boolean
}

export interface EventsBlock {
  layers: EventLayer[]
  notices?: string[]
  ref?: { project?: string; obj_name?: string }
}

export const EVENTS_CONFIG = CONFIG as {
  kinds: EventKind[]
  looks: EventLook[]
  labels: EventLabels[]
  who_can_add: Array<'viewers' | 'admins'>
  map_fields: Array<keyof EventMap>
  palette: string[]
  defaults: { look: EventLook; labels: EventLabels; who_can_add: 'viewers' | 'admins' }
  limits: {
    layers_max: number
    items_max: number
    query_rows_max: number
    label_max: number
    text_max: number
    name_max: number
    sql_max: number
  }
}

const ID_RE = /^[A-Za-z0-9_-]{1,40}$/
const COLOR_RE = /^#[0-9a-fA-F]{6}$/
const COLUMN_RE = /^[A-Za-z_][A-Za-z0-9_$#.]{0,127}$/
const DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

const lim = () => EVENTS_CONFIG.limits

function text(value: unknown, max: number): string {
  if (value == null || typeof value === 'object') return ''
  const s = String(value).trim()
  return max > 0 ? s.slice(0, max) : s
}

// ── dates ────────────────────────────────────────────────────────────────
// A naive calendar value, held as UTC milliseconds (no time-zone maths: the
// chart's axis is the calendar the database stored).

export function toMillis(value: unknown): number | null {
  if (value == null || typeof value === 'boolean') return null
  if (value instanceof Date) {
    const t = value.getTime()
    return Number.isNaN(t) ? null : t
  }
  if (typeof value !== 'string') return null
  const m = DATE_RE.exec(value.trim())
  if (!m) return null
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  const hh = Number(m[4] || 0), mi = Number(m[5] || 0), ss = Number(m[6] || 0)
  if (mo < 1 || mo > 12 || hh > 23 || mi > 59 || ss > 59) return null
  const t = Date.UTC(y, mo - 1, d, hh, mi, ss)
  const back = new Date(t)
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null
  return t
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

export function isoOf(ms: number): string {
  const d = new Date(ms)
  const date = `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  if (!d.getUTCHours() && !d.getUTCMinutes() && !d.getUTCSeconds()) return date
  return `${date}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
}

export function normDate(value: unknown): string {
  const t = toMillis(value)
  return t == null ? '' : isoOf(t)
}

// ── items / layers ───────────────────────────────────────────────────────

export function normalizeItem(raw: unknown): EventItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const start = toMillis(r.date)
  const label = text(r.label, lim().label_max)
  if (start == null || !label) return null
  let end = toMillis(r.end)
  if (end != null && end < start) end = null
  return {
    date: isoOf(start),
    end: end != null ? isoOf(end) : '',
    label,
    category: text(r.category, lim().label_max),
    note: text(r.note, lim().text_max),
  }
}

export function normalizeItems(raw: unknown): EventItem[] {
  if (!Array.isArray(raw)) return []
  const out: EventItem[] = []
  for (const r of raw) {
    const it = normalizeItem(r)
    if (it) {
      out.push(it)
      if (out.length >= lim().items_max) break
    }
  }
  return out
}

function enumOf<T extends string>(value: unknown, choices: T[], dflt: T): T {
  const s = String(value ?? '').trim().toLowerCase() as T
  return choices.includes(s) ? s : dflt
}

function column(value: unknown): string {
  const s = text(value, 128)
  return COLUMN_RE.test(s) ? s : ''
}

export function normalizeLayer(raw: unknown, index: number): EventLayer | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const kind = String(r.kind ?? '').trim().toLowerCase() as EventKind
  if (!EVENTS_CONFIG.kinds.includes(kind)) return null
  const d = EVENTS_CONFIG.defaults
  const palette = EVENTS_CONFIG.palette.length ? EVENTS_CONFIG.palette : ['#475569']
  const rid = String(r.id ?? '').trim()
  const color = String(r.color ?? '').trim()
  const layer: EventLayer = {
    id: ID_RE.test(rid) ? rid : `ev${index + 1}`,
    kind,
    name: text(r.name, lim().name_max) || 'Events',
    color: COLOR_RE.test(color) ? color.toLowerCase() : palette[index % palette.length],
    look: enumOf(r.look, EVENTS_CONFIG.looks, d.look),
    labels: enumOf(r.labels, EVENTS_CONFIG.labels, d.labels),
    hidden: r.hidden === true,
  }
  if (kind === 'manual') layer.items = normalizeItems(r.items)
  else if (kind === 'column') layer.column = column(r.column)
  else if (kind === 'query') {
    let sql = text(r.sql, lim().sql_max)
    while (sql.endsWith(';')) sql = sql.slice(0, -1).trimEnd()
    const mraw = (r.map && typeof r.map === 'object' && !Array.isArray(r.map) ? r.map : {}) as Record<string, unknown>
    layer.sql = sql
    const map = {} as EventMap
    for (const f of EVENTS_CONFIG.map_fields) map[f] = column(mraw[f])
    layer.map = map
  } else if (kind === 'notes') {
    layer.who_can_add = enumOf(r.who_can_add, EVENTS_CONFIG.who_can_add, d.who_can_add)
  }
  return layer
}

export function normalizeEvents(raw: unknown): EventsBlock {
  const layersRaw = raw && typeof raw === 'object' ? (raw as { layers?: unknown }).layers : undefined
  if (!Array.isArray(layersRaw)) return { layers: [] }
  const out: EventLayer[] = []
  const seen = new Set<string>()
  const single = new Set<string>()
  layersRaw.forEach((r, i) => {
    if (out.length >= lim().layers_max) return
    const layer = normalizeLayer(r, i)
    if (!layer) return
    if (layer.kind === 'column' || layer.kind === 'notes') {
      if (single.has(layer.kind)) return
      single.add(layer.kind)
    }
    const base = layer.id
    let n = 2
    while (seen.has(layer.id)) {
      layer.id = `${base.slice(0, 36)}_${n}`
      n += 1
    }
    seen.add(layer.id)
    out.push(layer)
  })
  return { layers: out }
}

// ── the chart's date range (parity with the engine) ──────────────────────

function rowsColumn(visualLayout: unknown): string {
  const groups = (visualLayout as { ColumnGroups?: unknown[] } | null)?.ColumnGroups
  for (const g of groups ?? []) {
    if (!g || typeof g !== 'object') continue
    const gg = g as { StandardName?: unknown; COLUMNS?: Array<{ NAME?: unknown }> }
    if (String(gg.StandardName ?? '').trim().toLowerCase() !== 'rows') continue
    for (const c of gg.COLUMNS ?? []) {
      if (c && String(c.NAME ?? '').trim()) return String(c.NAME).trim()
    }
  }
  return ''
}

export function chartDateRange(jsonData: unknown, visualLayout: unknown): [number, number] | null {
  if (!Array.isArray(jsonData) || jsonData.length < 2) return null
  const name = rowsColumn(visualLayout)
  if (!name) return null
  const lower = name.toLowerCase()
  const dates = new Set<number>()
  for (const row of jsonData.slice(1)) {
    if (!row || typeof row !== 'object') continue
    const rr = row as Record<string, unknown>
    let val = rr[name]
    if (val == null) {
      for (const [k, v] of Object.entries(rr)) {
        if (k.toLowerCase() === lower) { val = v; break }
      }
    }
    const t = toMillis(val)
    if (t != null) dates.add(t)
  }
  if (!dates.size) return null
  const ordered = [...dates].sort((a, b) => a - b)
  const gaps = ordered.slice(1).map((b, i) => b - ordered[i]).sort((a, b) => a - b)
  let step = gaps.length ? gaps[Math.floor((gaps.length - 1) / 2)] : 86400000
  if (step <= 0) step = 86400000
  return [ordered[0], ordered[ordered.length - 1] + step]
}

export function splitRange(items: EventItem[], rng: [number, number] | null): { inside: EventItem[]; outside: number } {
  if (!rng) return { inside: [...items], outside: 0 }
  const [lo, hi] = rng
  const inside: EventItem[] = []
  let outside = 0
  for (const it of items) {
    const start = toMillis(it.date)
    if (start == null) continue
    const end = toMillis(it.end) ?? start
    if (start < hi && end >= lo) inside.push(it)
    else outside += 1
  }
  return { inside, outside }
}

export function rowsToItems(rows: Array<Record<string, unknown>>, mapping: Partial<EventMap>): EventItem[] {
  const out: EventItem[] = []
  for (const row of rows ?? []) {
    if (!row || typeof row !== 'object') continue
    const lowered: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(row)) lowered[k.toLowerCase()] = v
    const raw: Record<string, unknown> = {}
    for (const f of EVENTS_CONFIG.map_fields) {
      const col = String(mapping[f] ?? '')
      raw[f] = col ? lowered[col.toLowerCase()] : null
    }
    const it = normalizeItem(raw)
    if (it) {
      out.push(it)
      if (out.length >= lim().items_max) break
    }
  }
  return out
}

/** A fresh layer of a kind, for the designer's Add events menu. */
export function newLayer(kind: EventKind, existing: EventLayer[], name?: string): EventLayer {
  const used = new Set(existing.map((l) => l.color))
  const color = EVENTS_CONFIG.palette.find((c) => !used.has(c)) ?? EVENTS_CONFIG.palette[0]
  const ids = new Set(existing.map((l) => l.id))
  let n = existing.length + 1
  while (ids.has(`ev${n}`)) n += 1
  const d = EVENTS_CONFIG.defaults
  const base: EventLayer = {
    id: `ev${n}`, kind, name: name || 'Events', color,
    look: kind === 'notes' ? 'flag' : d.look, labels: d.labels, hidden: false,
  }
  if (kind === 'manual') base.items = []
  if (kind === 'column') base.column = ''
  if (kind === 'query') {
    base.sql = ''
    base.map = { date: '', end: '', label: '', category: '', note: '' }
  }
  if (kind === 'notes') base.who_can_add = d.who_can_add
  return base
}
